// frontend/src/features/tasks/RecurrenceSelector.tsx

import {
  Stack,
  Group,
  NumberInput,
  Select,
  Switch,
  Text,
  Alert,
  Paper,
  Collapse,
  SegmentedControl,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { DayPicker } from './DayPicker';
import {
  type Recurrence,
  type RecurrenceUnit,
  type MonthlyType,
  RECURRENCE_UNIT_OPTIONS,
  DAYS_OF_WEEK,
  NEW_WEEK_OF_MONTH_OPTIONS,
  isRollingRecurrence,
  buildRecurrenceSummary,
} from '@/types/task';

interface RecurrenceSelectorProps {
  value: Recurrence;
  onChange: (value: Recurrence) => void;
  taskName?: string;
}

export function RecurrenceSelector({ value, onChange, taskName = '' }: RecurrenceSelectorProps) {
  const updateField = <K extends keyof Recurrence>(
    field: K,
    fieldValue: Recurrence[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const rolling = isRollingRecurrence(value);
  const pinned = !rolling;

  // Toggle label adapts to unit
  const pinLabel = value.unit === 'week' ? 'Pin to specific days' : 'Pin to a specific date';
  const pinSubtitle = pinned
    ? 'On \u2014 always due on the same day, even if you\'re late'
    : 'Off \u2014 due date shifts based on when you complete it';

  // Determine monthly type from current value
  const monthlyType: MonthlyType =
    value.week_of_month != null ? 'nth_weekday' : 'day_of_month';

  const handleMonthlyTypeChange = (type: MonthlyType) => {
    if (type === 'day_of_month') {
      onChange({
        ...value,
        day_of_month: value.day_of_month ?? 1,
        week_of_month: undefined,
        days_of_week: [],
      });
    } else {
      onChange({
        ...value,
        day_of_month: undefined,
        week_of_month: value.week_of_month ?? 1,
        days_of_week: value.days_of_week.length > 0 ? value.days_of_week : [5],
      });
    }
  };

  // Handle toggle: pin ON sets a default day; pin OFF clears fixed fields
  const handlePinToggle = (checked: boolean) => {
    if (checked) {
      // Turn on pinning - set sensible defaults
      if (value.unit === 'week') {
        // Default to current day of week (or Saturday)
        const today = new Date().getDay();
        // JS getDay: 0=Sun, convert to our 0=Mon
        const dayOfWeek = today === 0 ? 6 : today - 1;
        onChange({ ...value, days_of_week: [dayOfWeek] });
      } else if (value.unit === 'month') {
        onChange({ ...value, day_of_month: 1 });
      }
    } else {
      // Turn off pinning - clear fixed fields
      onChange({
        ...value,
        days_of_week: [],
        day_of_month: undefined,
        week_of_month: undefined,
      });
    }
  };

  // Build monthly options dropdown
  const monthlyOptions = [
    {
      value: 'day_of_month',
      label: `on day ${value.day_of_month ?? 1}`,
    },
    {
      value: 'nth_weekday',
      label: `on the ${NEW_WEEK_OF_MONTH_OPTIONS.find((w) => w.value === (value.week_of_month ?? 1))?.label ?? '1st'} ${DAYS_OF_WEEK.find((d) => d.value === (value.days_of_week[0] ?? 5))?.fullLabel ?? 'Saturday'}`,
    },
  ];

  const showDayWarning =
    value.unit === 'month' &&
    monthlyType === 'day_of_month' &&
    value.day_of_month != null &&
    value.day_of_month >= 29;

  // Build preview summary
  const summary = buildRecurrenceSummary(value, taskName);

  // Interval display for behavior hint
  const unitLabel = value.interval === 1
    ? RECURRENCE_UNIT_OPTIONS.find(u => u.value === value.unit)?.label ?? value.unit
    : RECURRENCE_UNIT_OPTIONS.find(u => u.value === value.unit)?.labelPlural ?? value.unit;
  const intervalStr = value.interval === 1 ? unitLabel : `${value.interval} ${unitLabel}`;

  return (
    <Stack gap="sm">
      {/* Every [N] [unit] */}
      <Group gap="xs" align="flex-end">
        <Text size="sm" fw={500} style={{ paddingBottom: 6 }}>
          Every
        </Text>
        <NumberInput
          w={70}
          min={1}
          max={99}
          value={value.interval}
          onChange={(v) => updateField('interval', typeof v === 'number' ? v : 1)}
        />
        <Select
          w={100}
          data={RECURRENCE_UNIT_OPTIONS.map((u) => ({
            value: u.value,
            label: value.interval === 1 ? u.label : u.labelPlural,
          }))}
          value={value.unit}
          onChange={(v) => {
            if (v) {
              const newUnit = v as RecurrenceUnit;
              onChange({
                interval: value.interval,
                unit: newUnit,
                days_of_week: [],
                day_of_month: newUnit === 'month' ? 1 : undefined,
                week_of_month: undefined,
                time_of_day: value.time_of_day,
                max_recurrences: value.max_recurrences,
              });
            }
          }}
        />
      </Group>

      {/* Toggle: Pin to specific days/dates */}
      {value.unit !== 'day' && (
        <Paper p="sm" radius="md" withBorder bg="gray.0">
          <Group justify="space-between" wrap="nowrap">
            <div>
              <Text size="sm" fw={600}>{pinLabel}</Text>
              <Text size="xs" c="dimmed">{pinSubtitle}</Text>
            </div>
            <Switch
              checked={pinned}
              onChange={(e) => handlePinToggle(e.currentTarget.checked)}
              aria-label={pinLabel}
            />
          </Group>
        </Paper>
      )}

      {/* Rolling behavior hint (when toggle is OFF) */}
      {rolling && (
        <Alert variant="light" color="gray" radius="md" p="xs">
          <Text size="xs" c="dimmed">
            Due {intervalStr} after you complete it each time. Finish late? The next one shifts accordingly.
          </Text>
        </Alert>
      )}

      {/* Fixed schedule panel (when toggle is ON) */}
      <Collapse in={pinned}>
        <Paper p="md" radius="md" withBorder>
          {/* Weekly: Day picker */}
          {value.unit === 'week' && (
            <Stack gap="xs">
              <Text size="sm" fw={600} c="dimmed">Which day?</Text>
              <DayPicker
                selectedDays={value.days_of_week}
                onChange={(days) => updateField('days_of_week', days)}
              />
            </Stack>
          )}

          {/* Monthly: dropdown for day vs nth weekday */}
          {value.unit === 'month' && (
            <Stack gap="xs">
              <Text size="sm" fw={600} c="dimmed">Which date?</Text>
              <Group gap="xs" align="flex-end">
                <Select
                  w={220}
                  data={monthlyOptions}
                  value={monthlyType}
                  onChange={(v) => handleMonthlyTypeChange(v as MonthlyType)}
                />

                {monthlyType === 'day_of_month' && (
                  <NumberInput
                    w={70}
                    min={1}
                    max={31}
                    value={value.day_of_month ?? 1}
                    onChange={(v) =>
                      updateField('day_of_month', typeof v === 'number' ? v : 1)
                    }
                  />
                )}

                {monthlyType === 'nth_weekday' && (
                  <>
                    <Select
                      w={90}
                      data={NEW_WEEK_OF_MONTH_OPTIONS.map((w) => ({
                        value: String(w.value),
                        label: w.label,
                      }))}
                      value={String(value.week_of_month ?? 1)}
                      onChange={(v) =>
                        updateField('week_of_month', v ? parseInt(v, 10) : 1)
                      }
                    />
                    <Select
                      w={120}
                      data={DAYS_OF_WEEK.map((d) => ({
                        value: String(d.value),
                        label: d.fullLabel,
                      }))}
                      value={String(value.days_of_week[0] ?? 5)}
                      onChange={(v) =>
                        updateField('days_of_week', v ? [parseInt(v, 10)] : [5])
                      }
                    />
                  </>
                )}
              </Group>

              {showDayWarning && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="yellow"
                  variant="light"
                >
                  Not all months have day {value.day_of_month}. Task will run on the
                  last day of shorter months.
                </Alert>
              )}
            </Stack>
          )}

          {/* Time (inside fixed panel) */}
          <Group gap="xs" align="flex-end" mt="sm">
            <Text size="sm" fw={500} style={{ paddingBottom: 6 }}>
              At
            </Text>
            <TimeInput
              w={100}
              value={value.time_of_day ?? ''}
              onChange={(e) => updateField('time_of_day', e.currentTarget.value || undefined)}
              placeholder="09:00"
            />
            <Text size="sm" c="dimmed" style={{ paddingBottom: 6 }}>
              (optional)
            </Text>
          </Group>

          {/* Fixed schedule note */}
          <Alert variant="light" color="orange" radius="md" mt="sm" p="xs">
            <Text size="xs">
              This task will stay on schedule even if you complete it late.
            </Text>
          </Alert>
        </Paper>
      </Collapse>

      {/* Time picker for rolling mode (outside the fixed panel) */}
      {rolling && (
        <Group gap="xs" align="flex-end">
          <Text size="sm" fw={500} style={{ paddingBottom: 6 }}>
            At
          </Text>
          <TimeInput
            w={100}
            value={value.time_of_day ?? ''}
            onChange={(e) => updateField('time_of_day', e.currentTarget.value || undefined)}
            placeholder="09:00"
          />
          <Text size="sm" c="dimmed" style={{ paddingBottom: 6 }}>
            (optional)
          </Text>
        </Group>
      )}

      {/* Ends after section */}
      <Paper p="sm" radius="md" withBorder bg="gray.0">
        <Stack gap="xs">
          <Text size="sm" fw={600}>Ends</Text>
          <SegmentedControl
            value={value.max_recurrences != null ? 'after' : 'never'}
            onChange={(v) => {
              if (v === 'never') {
                onChange({ ...value, max_recurrences: undefined });
              } else {
                onChange({ ...value, max_recurrences: value.max_recurrences ?? 4 });
              }
            }}
            data={[
              { value: 'never', label: 'Never' },
              { value: 'after', label: 'After...' },
            ]}
            size="xs"
          />
          <Collapse in={value.max_recurrences != null}>
            <Group gap="xs" align="flex-end">
              <Text size="sm" style={{ paddingBottom: 6 }}>After</Text>
              <NumberInput
                w={70}
                min={2}
                max={999}
                value={value.max_recurrences ?? ''}
                onChange={(v) =>
                  updateField('max_recurrences', typeof v === 'number' ? v : undefined)
                }
              />
              <Text size="sm" style={{ paddingBottom: 6 }}>completions</Text>
            </Group>
          </Collapse>
        </Stack>
      </Paper>

      {/* Preview banner */}
      <Alert
        variant="light"
        color="blue"
        radius="md"
        icon={<IconInfoCircle size={16} />}
      >
        <Text size="sm" fw={500}>{summary.headline}</Text>
        <Text size="xs" c="dimmed" mt={4}>{summary.detail}</Text>
      </Alert>
    </Stack>
  );
}
